/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Close, ExpandMore } from "@mui/icons-material";
import { Theme as MaterialTheme, IconButton, InputAdornment, Stack, TextField, Grid, InputLabel, FormControl, Box, Select, MenuItem, useMediaQuery, Divider, Accordion, AccordionSummary, AccordionDetails, useTheme, SelectChangeEvent, Button } from "@mui/material";

import { toast, } from 'react-toastify';

import DateRangePopper from '../../../common/components/DateRangePopper';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';
import SourceDocumentBusinessLogic from '../../../businessLogic/SourceDocumentBusinessLogic';
import { digiAccGuiStates, digiAccGuiTypes } from '../../../common/TextTypes';
import { IFileSortItem } from '../../../service/model/ISourceDocumentModel';

interface IProps {
    page: number,
    limit: number,
    voucherDateFrom: Date | undefined,
    handleVoucherDateFrom: (newDate?: Date) => void,
    voucherDateTo: Date | undefined,
    handleVoucherDateTo: (newDate?: Date) => void,
    dueDateFrom: Date | undefined,
    handleDueDateFrom: (newDate?: Date) => void,
    dueDateTo: Date | undefined,
    handleDueDateTo: (newDate?: Date) => void,
    valueDateFrom: Date | undefined,
    handleValueDateFrom: (newDate?: Date) => void,
    valueDateTo: Date | undefined,
    handleValueDateTo: (newDate?: Date) => void,
    createdDateFrom: Date | undefined,
    handleCreatedDateFrom: (newDate?: Date) => void,
    createdDateTo: Date | undefined,
    handleCreatedDateTo: (newDate?: Date) => void,
    voucherType: digiAccGuiTypes,
    handleVoucherTypeChange: (event: SelectChangeEvent) => void,
    voucherStatus: digiAccGuiStates | undefined,
    handleVoucherStatusChange: (event: SelectChangeEvent) => void,
    invoiceId: string,
    setInvoiceId: Dispatch<SetStateAction<string>>,
    debetId: string,
    setDebetId: Dispatch<SetStateAction<string>>,
    creditId: string,
    setCreditId: Dispatch<SetStateAction<string>>,
    departmentId: string,
    setDepartmentId: Dispatch<SetStateAction<string>>,
    projectId: string,
    setProjectId: Dispatch<SetStateAction<string>>,
    voucherIdFrom: string,
    setVoucherIdFrom: Dispatch<SetStateAction<string>>,
    voucherIdTo: string,
    setVoucherIdTo: Dispatch<SetStateAction<string>>,
    amountFrom: string,
    setAmountFrom: Dispatch<SetStateAction<string>>,
    amountTo: string,
    setAmountTo: Dispatch<SetStateAction<string>>,
    freeTextSearch: string,
    setFreeTextSearch: Dispatch<SetStateAction<string>>,
    sortDocumentField?: IFileSortItem,
}

const SearchBar = (props: IProps): JSX.Element => {

    const theme = useTheme();

    const { organizationId } = useParams();

    const [loader, setLoader] = useState(false);

    const { t } = useTranslation();

    const isLgAndUpScreen = useMediaQuery(theme.breakpoints.up('lg')) //Check if user has LgScreen and up
    const isSmAndUpScreen = useMediaQuery(theme.breakpoints.up('sm')) //Check if user has MdScreen and up

    const notANumber = () => toast(t("search.onlyNumbers"))
    function onlyNumbers(number: string, setState: Dispatch<SetStateAction<string>>) {
        if (number !== "" && !/^\d+$/.test(number)) {
            notANumber();
        } else {
            setState(number);
        }
    }

    const {
        page,
        limit,
        voucherDateFrom,
        handleVoucherDateFrom,
        voucherDateTo,
        handleVoucherDateTo,
        dueDateFrom,
        handleDueDateFrom,
        dueDateTo,
        handleDueDateTo,
        valueDateFrom,
        handleValueDateFrom,
        valueDateTo,
        handleValueDateTo,
        createdDateFrom,
        handleCreatedDateFrom,
        createdDateTo,
        handleCreatedDateTo,
        voucherType,
        handleVoucherTypeChange,
        voucherStatus,
        handleVoucherStatusChange,
        invoiceId,
        setInvoiceId,
        debetId,
        setDebetId,
        creditId,
        setCreditId,
        departmentId,
        setDepartmentId,
        projectId,
        setProjectId,
        voucherIdFrom,
        setVoucherIdFrom,
        voucherIdTo,
        setVoucherIdTo,
        amountFrom,
        setAmountFrom,
        amountTo,
        setAmountTo,
        freeTextSearch,
        setFreeTextSearch,
        sortDocumentField } = props;

    const logicGetExcelFile = async () => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsExcelFile(
            organizationId,
            page,
            limit,
            voucherDateFrom,
            voucherDateTo,
            dueDateFrom,
            dueDateTo,
            valueDateFrom,
            valueDateTo,
            createdDateFrom,
            createdDateTo,
            voucherStatus ? [voucherStatus] : ["All"],
            voucherType,
            invoiceId,
            debetId !== "" ? parseInt(debetId) : undefined,
            creditId !== "" ? parseInt(creditId) : undefined,
            departmentId,
            projectId,
            voucherIdFrom,
            voucherIdTo,
            amountFrom,
            amountTo,
            freeTextSearch,
            sortDocumentField
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = document.createElement("a");
            link.download = "voucherSearch.xlsx";
            link.href = blobUrl;
            link.click();
        }
    }

    if (isLgAndUpScreen) {
        return <>
            <Grid container spacing={2} padding={theme.spacing(2)}>
                <Grid item xs={12} sm={6} lg={3}>
                    <DateRangePopper
                        fromDate={voucherDateFrom} handleFromDate={handleVoucherDateFrom}
                        toDate={voucherDateTo} handleToDate={handleVoucherDateTo}
                        dateName={t('search.voucherDate')} mobile={!isSmAndUpScreen} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <DateRangePopper
                        fromDate={dueDateFrom} handleFromDate={handleDueDateFrom}
                        toDate={dueDateTo} handleToDate={handleDueDateTo}
                        dateName={t('search.dueDate')} mobile={!isSmAndUpScreen} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <DateRangePopper
                        fromDate={valueDateFrom} handleFromDate={handleValueDateFrom}
                        toDate={valueDateTo} handleToDate={handleValueDateTo}
                        dateName={t('search.valueDate')} mobile={!isSmAndUpScreen} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <DateRangePopper
                        fromDate={createdDateFrom} handleFromDate={handleCreatedDateFrom}
                        toDate={createdDateTo} handleToDate={handleCreatedDateTo}
                        dateName={t('search.created')} mobile={!isSmAndUpScreen} />
                </Grid>
            </Grid>
            <Grid container spacing={2} padding={theme.spacing(2)} justifyContent={isLgAndUpScreen ? "unset" : "center"}>
                <Grid item>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={theme.spacing(2)}>
                        <Box sx={{ minWidth: "12rem" }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="select-status">{t('search.status')}</InputLabel>
                                <Select
                                    labelId="select-status-label"
                                    id="select-status"
                                    value={voucherStatus}
                                    label={t('search.status')}
                                    onChange={handleVoucherStatusChange}
                                >
                                    <MenuItem value={"All"}>{t("status.all")}</MenuItem>
                                    <MenuItem value={"Transferred"}>{t("status.transferred")}</MenuItem>
                                    <MenuItem value={"AdditionTransferred"}>{t("status.additionTransferred")}</MenuItem>
                                    <MenuItem value={"ApprovalPending"}>{t("status.approvalPending")}</MenuItem>
                                    <MenuItem value={"ApprovalPostponed"}>{t("status.approvalPostponed")}</MenuItem>
                                    <MenuItem value={"AwaitingForDataEntry"}>{t("status.awaitingForDataEntry")}</MenuItem>
                                    <MenuItem value={"AwaitingForTransfer"}>{t("status.awaitingForTransfer")}</MenuItem>
                                    <MenuItem value={"InProgress"}>{t("status.inProgress")}</MenuItem>
                                    <MenuItem value={"Rejected"}>{t("status.rejected")}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ minWidth: "12rem" }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="select-type">Type</InputLabel>
                                <Select
                                    labelId="select-type-label"
                                    id="select-type"
                                    value={voucherType}
                                    label="Type"
                                    onChange={handleVoucherTypeChange}
                                >
                                    <MenuItem value={"All"}>{t("types.all")}</MenuItem>
                                    <MenuItem value={"IncomingInvoice"}>{t("types.incomingInvoice")}</MenuItem>
                                    <MenuItem value={"OutgoingInvoice"}>{t("types.outgoingInvoice")}</MenuItem>
                                    <MenuItem value={"Banking"}>{t("types.banking")}</MenuItem>
                                    <MenuItem value={"Cash"}>{t("types.cash")}</MenuItem>
                                    <MenuItem value={"Statement"}>{t("types.statement")}</MenuItem>
                                    <MenuItem value={"Addition"}>{t("types.addition")}</MenuItem>
                                    <MenuItem value={"JournalEntries"}>{t("types.journalEntries")}</MenuItem>
                                    <MenuItem value={"Miscellaneous"}>{t("types.miscellaneous")}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item>
                    <TextField size="small" variant="outlined" color="warning" value={debetId} sx={{ width: "12rem", }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setDebetId("")}
                                    sx={{ display: debetId ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => onlyNumbers(e.target.value, setDebetId)}
                        id="searchDebetId" label={t("search.debet")} />
                </Grid>
                <Grid item>
                    <TextField size="small" variant="outlined" color="warning" value={creditId} sx={{ width: "12rem", }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setCreditId("")}
                                    sx={{ display: creditId ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => onlyNumbers(e.target.value, setCreditId)}
                        id="searchCreditId" label={t("search.credit")} />
                </Grid>
                <Grid item>
                    <TextField size="small" variant="outlined" color="warning"
                        sx={{ width: "12rem" }}
                        value={invoiceId}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setInvoiceId("")}
                                    sx={{ display: invoiceId ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            setInvoiceId(inputValue); 
                        }}
                        id="searchProdNo" label={t('search.invoiceId')} />
                </Grid>
                <Grid item>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: theme.spacing(2), sm: 0 }}>
                        <TextField size="small" variant="outlined" color="warning" value={amountFrom}
                            sx=
                            {{
                                width: "12rem",
                                [theme.breakpoints.up('sm')]: { fieldset: { borderBottomRightRadius: 0, borderTopRightRadius: 0, } },
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end" component={IconButton} onClick={() => setAmountFrom('')}
                                        sx={{ display: amountFrom ? "inherit" : "none" }}><Close /></InputAdornment>,
                            }}
                            onChange={(e) => onlyNumbers(e.target.value, setAmountFrom)}
                            id="searchAmountFrom" label={t('search.amountFrom')} />
                        <TextField size="small" variant="outlined" color="warning" value={amountTo}
                            sx=
                            {{
                                width: "12rem",
                                [theme.breakpoints.up('sm')]: { fieldset: { borderBottomLeftRadius: 0, borderTopLeftRadius: 0, } },
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end" component={IconButton} onClick={() => setAmountTo('')}
                                        sx={{ display: amountTo ? "inherit" : "none" }}><Close /></InputAdornment>,
                            }}
                            onChange={(e) => onlyNumbers(e.target.value, setAmountTo)}
                            id="searchAmountTo" label={t('search.amountTo')} />
                    </Stack>
                </Grid>
                <Grid item>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: theme.spacing(2), sm: 0 }}>
                        <TextField size="small" variant="outlined" color="warning" value={voucherIdFrom}
                            sx=
                            {{
                                width: "12rem",
                                [theme.breakpoints.up('sm')]: { fieldset: { borderBottomRightRadius: 0, borderTopRightRadius: 0, } },
                            }}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end" component={IconButton} onClick={() => setVoucherIdFrom(() => "")}
                                        sx={{ display: voucherIdFrom ? "inherit" : "none" }}><Close /></InputAdornment>,
                            }}
                            onChange={(e) => onlyNumbers(e.target.value, setVoucherIdFrom)}
                            id="searchVoucherIdFrom" label={t('search.voucherIdFrom')} />
                        <TextField size="small" variant="outlined" color="warning" value={voucherIdTo}
                            sx=
                            {{
                                width: "12rem",
                                [theme.breakpoints.up('sm')]: { fieldset: { borderBottomLeftRadius: 0, borderTopLeftRadius: 0, } },
                            }}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end" component={IconButton} onClick={() => setVoucherIdTo('')}
                                        sx={{ display: voucherIdTo ? "inherit" : "none" }}><Close /></InputAdornment>,
                            }}
                            onChange={(e) => onlyNumbers(e.target.value, setVoucherIdTo)}
                            id="searchVoucherIdTo" label={t('search.voucherIdTo')} />
                    </Stack>
                </Grid>
                <Grid item>
                    <TextField size="small" variant="outlined" color="warning" value={departmentId}
                        sx={{ width: "12rem" }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setDepartmentId("")}
                                    sx={{ display: departmentId ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => setDepartmentId(e.target.value)}
                        id="searchDepartmentId" label={t('search.department')} />
                </Grid>
                <Grid item>
                    <TextField size="small" variant="outlined" color="warning" value={projectId}
                        sx={{ width: "12rem" }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setProjectId("")}
                                    sx={{ display: projectId ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => setProjectId(e.target.value)}
                        id="searchProjectId" label={t('search.project')} />
                </Grid>
                <Grid item display={isLgAndUpScreen ? "flex" : "none"}>
                    <TextField size="small" variant="outlined" color="warning" value={freeTextSearch}
                        sx={{ width: "22rem" }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setFreeTextSearch("")}
                                    sx={{ display: freeTextSearch ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => setFreeTextSearch(e.target.value)}
                        id="freeTextSearchId" label={t('search.freeTextSearch')} />
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={logicGetExcelFile}>
                        {t("search.excelExport")}
                    </Button>
                </Grid>
            </Grid>
            <Grid container>
                <Grid xs={12}>
                    <Divider orientation="horizontal" />
                </Grid>
            </Grid>
        </>
    } else {
        return <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                <TextField size="small" variant="outlined" color="warning" value={freeTextSearch}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment:
                            <InputAdornment position="end" component={IconButton} onClick={() => setFreeTextSearch("")}
                                sx={{ display: projectId ? "inherit" : "none" }}><Close /></InputAdornment>,
                    }}
                    onChange={(e) => setFreeTextSearch(e.target.value)}
                    id="freeTextSearchId" label={t('search.freeTextSearch')} />
            </AccordionSummary>
            <AccordionDetails>
                <>
                    <Grid container spacing={2} padding={theme.spacing(2)}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DateRangePopper
                                fromDate={voucherDateFrom} handleFromDate={handleVoucherDateFrom}
                                toDate={voucherDateTo} handleToDate={handleVoucherDateTo}
                                dateName={t('search.voucherDate')} mobile={!isSmAndUpScreen} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DateRangePopper
                                fromDate={dueDateFrom} handleFromDate={handleDueDateFrom}
                                toDate={dueDateTo} handleToDate={handleDueDateTo}
                                dateName={t('search.dueDate')} mobile={!isSmAndUpScreen} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DateRangePopper
                                fromDate={valueDateFrom} handleFromDate={handleValueDateFrom}
                                toDate={valueDateTo} handleToDate={handleValueDateTo}
                                dateName={t('search.valueDate')} mobile={!isSmAndUpScreen} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DateRangePopper
                                fromDate={createdDateFrom} handleFromDate={handleCreatedDateFrom}
                                toDate={createdDateTo} handleToDate={handleCreatedDateTo}
                                dateName={t('search.created')} mobile={!isSmAndUpScreen} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} padding={theme.spacing(2)} justifyContent={isLgAndUpScreen ? "unset" : "center"}>
                        <Grid item>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={theme.spacing(2)}>
                                <Box sx={{ minWidth: "12rem" }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="select-status">{t('search.status')}</InputLabel>
                                        <Select
                                            labelId="select-status-label"
                                            id="select-status"
                                            value={voucherStatus}
                                            label={t('search.status')}
                                            onChange={handleVoucherStatusChange}
                                        >
                                            <MenuItem value={"All"}>{t("status.all")}</MenuItem>
                                            <MenuItem value={"Transferred"}>{t("status.transferred")}</MenuItem>
                                            <MenuItem value={"AdditionTransferred"}>{t("status.additionTransferred")}</MenuItem>
                                            <MenuItem value={"ApprovalPending"}>{t("status.approvalPending")}</MenuItem>
                                            <MenuItem value={"ApprovalPostponed"}>{t("status.approvalPostponed")}</MenuItem>
                                            <MenuItem value={"AwaitingForDataEntry"}>{t("status.awaitingForDataEntry")}</MenuItem>
                                            <MenuItem value={"AwaitingForTransfer"}>{t("status.awaitingForTransfer")}</MenuItem>
                                            <MenuItem value={"InProgress"}>{t("status.inProgress")}</MenuItem>
                                            <MenuItem value={"Rejected"}>{t("status.rejected")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ minWidth: "12rem" }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="select-type">Type</InputLabel>
                                        <Select
                                            labelId="select-type-label"
                                            id="select-type"
                                            value={voucherType}
                                            label="Type"
                                            onChange={handleVoucherTypeChange}
                                        >
                                            <MenuItem value={"All"}>{t("types.all")}</MenuItem>
                                            <MenuItem value={"IncomingInvoice"}>{t("types.incomingInvoice")}</MenuItem>
                                            <MenuItem value={"OutgoingInvoice"}>{t("types.outgoingInvoice")}</MenuItem>
                                            <MenuItem value={"Banking"}>{t("types.banking")}</MenuItem>
                                            <MenuItem value={"Cash"}>{t("types.cash")}</MenuItem>
                                            <MenuItem value={"Statement"}>{t("types.statement")}</MenuItem>
                                            <MenuItem value={"Addition"}>{t("types.addition")}</MenuItem>
                                            <MenuItem value={"JournalEntries"}>{t("types.journalEntries")}</MenuItem>
                                            <MenuItem value={"Miscellaneous"}>{t("types.miscellaneous")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item>
                            <TextField size="small" variant="outlined" color="warning" value={debetId} sx={{ width: "12rem", }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setDebetId("")}
                                            sx={{ display: debetId ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => onlyNumbers(e.target.value, setDebetId)}
                                id="searchDebetId" label={t("search.debet")} />
                        </Grid>
                        <Grid item>
                            <TextField size="small" variant="outlined" color="warning" value={creditId} sx={{ width: "12rem", }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setCreditId("")}
                                            sx={{ display: creditId ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => onlyNumbers(e.target.value, setCreditId)}
                                id="searchCreditId" label={t("search.credit")} />
                        </Grid>
                        <Grid item>
                            <TextField size="small" variant="outlined" color="warning"
                                sx={{ width: "12rem" }}
                                value={invoiceId}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setInvoiceId("")}
                                            sx={{ display: invoiceId ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => onlyNumbers(e.target.value, setInvoiceId)}
                                id="searchProdNo" label={t('search.invoiceId')} />
                        </Grid>
                        <Grid item>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: theme.spacing(2), sm: 0 }}>
                                <TextField size="small" variant="outlined" color="warning" value={amountFrom}
                                    sx=
                                    {{
                                        width: "12rem",
                                        [theme.breakpoints.up('sm')]: { fieldset: { borderBottomRightRadius: 0, borderTopRightRadius: 0, } },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    InputProps={{
                                        endAdornment:
                                            <InputAdornment position="end" component={IconButton} onClick={() => setAmountFrom('')}
                                                sx={{ display: amountFrom ? "inherit" : "none" }}><Close /></InputAdornment>,
                                    }}
                                    onChange={(e) => onlyNumbers(e.target.value, setAmountFrom)}
                                    id="searchAmountFrom" label={t('search.amountFrom')} />
                                <TextField size="small" variant="outlined" color="warning" value={amountTo}
                                    sx=
                                    {{
                                        width: "12rem",
                                        [theme.breakpoints.up('sm')]: { fieldset: { borderBottomLeftRadius: 0, borderTopLeftRadius: 0, } },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    InputProps={{
                                        endAdornment:
                                            <InputAdornment position="end" component={IconButton} onClick={() => setAmountTo('')}
                                                sx={{ display: amountTo ? "inherit" : "none" }}><Close /></InputAdornment>,
                                    }}
                                    onChange={(e) => onlyNumbers(e.target.value, setAmountTo)}
                                    id="searchAmountTo" label={t('search.amountTo')} />
                            </Stack>
                        </Grid>
                        <Grid item>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: theme.spacing(2), sm: 0 }}>
                                <TextField size="small" variant="outlined" color="warning" value={voucherIdFrom}
                                    sx=
                                    {{
                                        width: "12rem",
                                        [theme.breakpoints.up('sm')]: { fieldset: { borderBottomRightRadius: 0, borderTopRightRadius: 0, } },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment:
                                            <InputAdornment position="end" component={IconButton} onClick={() => setVoucherIdFrom(() => "")}
                                                sx={{ display: voucherIdFrom ? "inherit" : "none" }}><Close /></InputAdornment>,
                                    }}
                                    onChange={(e) => onlyNumbers(e.target.value, setVoucherIdFrom)}
                                    id="searchVoucherIdFrom" label={t('search.voucherIdFrom')} />
                                <TextField size="small" variant="outlined" color="warning" value={voucherIdTo}
                                    sx=
                                    {{
                                        width: "12rem",
                                        [theme.breakpoints.up('sm')]: { fieldset: { borderBottomLeftRadius: 0, borderTopLeftRadius: 0, } },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment:
                                            <InputAdornment position="end" component={IconButton} onClick={() => setVoucherIdTo('')}
                                                sx={{ display: voucherIdTo ? "inherit" : "none" }}><Close /></InputAdornment>,
                                    }}
                                    onChange={(e) => onlyNumbers(e.target.value, setVoucherIdTo)}
                                    id="searchVoucherIdTo" label={t('search.voucherIdTo')} />
                            </Stack>
                        </Grid>
                        <Grid item>
                            <TextField size="small" variant="outlined" color="warning" value={departmentId}
                                sx={{ width: "12rem" }}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setDepartmentId("")}
                                            sx={{ display: departmentId ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => setDepartmentId(e.target.value)}
                                id="searchDepartmentId" label={t('search.department')} />
                        </Grid>
                        <Grid item>
                            <TextField size="small" variant="outlined" color="warning" value={projectId}
                                sx={{ width: "12rem" }}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setProjectId("")}
                                            sx={{ display: projectId ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => setProjectId(e.target.value)}
                                id="searchProjectId" label={t('search.project')} />
                        </Grid>
                        <Grid item display={isLgAndUpScreen ? "flex" : "none"}>
                            <TextField size="small" variant="outlined" color="warning" value={freeTextSearch}
                                sx={{ width: "22rem" }}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end" component={IconButton} onClick={() => setFreeTextSearch("")}
                                            sx={{ display: freeTextSearch ? "inherit" : "none" }}><Close /></InputAdornment>,
                                }}
                                onChange={(e) => setFreeTextSearch(e.target.value)}
                                id="freeTextSearchId" label={t('search.freeTextSearch')} />
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid xs={12}>
                            <Divider orientation="horizontal" />
                        </Grid>
                    </Grid>
                </>
            </AccordionDetails>
        </Accordion>
    } 
    
}
export default SearchBar;